<template>
    <ul :class="resolveListClass()">
        <li
            v-for="(item, index) in items"
            :key="resolveItemKey(item, index)"
            :class="resolveItemClass(item)"
            :data-item-id="resolveItemId(item)"
        >
            <component
                :is="resolveNodeTag(item)"
                :href="resolveNodeHref(item)"
                :title="resolveItemTitle(item) || null"
                :class="resolveLinkClass(item)"
                :data-item-id="resolveItemId(item)"
                @click="handleItemClick(item, $event)"
            >
                <slot
                    name="content"
                    :item="item"
                    :level="currentLevel"
                    :item-id="resolveItemId(item)"
                    :item-name="resolveItemName(item)"
                    :item-code="resolveItemCode(item)"
                    :item-svg="resolveItemSvg(item)"
                    :item-image="resolveItemImage(item)"
                    :item-signoff="resolveItemSignoff(item)"
                    :has-children="hasChildren(item)"
                    :is-active="isActive(item)"
                    :is-expanded="isExpanded(item)"
                >
                    <img
                        v-if="resolveItemImage(item)"
                        :src="resolveItemImage(item)"
                        :alt="resolveItemImageAlt(item)"
                        :style="imageStyle"
                        class="gh-menu__icon gh-menu__icon--image"
                    />

                    <svg-icon
                        v-if="resolveItemSvg(item)"
                        class="gh-menu__icon"
                        :icon-name="resolveItemSvg(item)"
                        :width="iconSize"
                        :height="iconSize"
                    ></svg-icon>

                    <gh-badge
                        v-if="resolveItemCode(item)"
                        :variant="badgeVariant"
                        class="gh-menu__code"
                    >
                        {{ resolveItemCode(item) }}
                    </gh-badge>

                    <span class="gh-menu__name">
                        {{ resolveItemName(item) }}
                    </span>
                </slot>

                <svg-icon
                    v-if="hasChildren(item) && showArrow"
                    class="arrow-icon"
                    icon-name="arrow-down-regular"
                    :width="14"
                    :height="14"
                ></svg-icon>
                <span
                    v-else-if="!hasChildren(item) && resolveItemSignoff(item)"
                    class="gh-menu__signoff"
                >
                    {{ resolveItemSignoff(item) }}
                </span>
            </component>

            <gh-menu
                v-if="hasChildren(item)"
                :items="resolveChildren(item)"
                :icon-size="iconSize"
                :badge-variant="badgeVariant"
                :show-arrow="showArrow"
                :collapsible="collapsible"
                :allow-multiple-expanded-parents="allowMultipleExpandedParents"
                @select-item="handleChildSelect"
                @toggle-item="handleChildToggle"
            >
                <template v-if="hasContentSlot" #content>
                    <slot name="content"></slot>
                </template>
            </gh-menu>
        </li>
    </ul>
</template>

<script setup lang="ts">
    import {
        computed,
        inject,
        onBeforeMount,
        provide,
        ref,
        watch,
        useSlots,
    } from 'vue';
    import GhBadge from '@/components/base/GhBadge.vue';
    import SvgIcon from '@/components/base/SvgIcon.vue';
    import {
        BadgeVariant,
        type MenuIdMap,
        type MenuItem,
        type MenuIdValue,
        type MenuSelectPayload,
        type MenuTogglePayload,
    } from '@/types/components/base-components.types';

    interface MenuRootContext {
        internalActiveIdMap: {
            value: MenuIdMap;
        };
        internalExpandedIdMap: {
            value: MenuIdMap;
        };
        sanitizeIdMap: (sourceMap?: MenuIdMap | null) => MenuIdMap;
        pruneMapByLevel: (
            sourceMap: MenuIdMap | null | undefined,
            level: number
        ) => MenuIdMap;
        isSameIdMap: (
            sourceMapA?: MenuIdMap | null,
            sourceMapB?: MenuIdMap | null
        ) => boolean;
    }

    // ----- props -----
    const props = withDefaults(
        defineProps<{
            items?: MenuItem[];
            activeId?: string | number;
            activeLevel?: number;
            iconSize?: number;
            showArrow?: boolean;
            collapsible?: boolean;
            allowMultipleExpandedParents?: boolean;
            expandedIdMap?: MenuIdMap | null;
            badgeVariant?: BadgeVariant;
        }>(),
        {
            items: () => [],
            activeId: '',
            activeLevel: 0,
            iconSize: 20,
            showArrow: true,
            collapsible: true,
            allowMultipleExpandedParents: false,
            expandedIdMap: null,
            badgeVariant: BadgeVariant.Gray,
        }
    );

    // ----- emits -----
    const emits = defineEmits<{
        (event: 'select-item', payload: MenuSelectPayload): void;
        (event: 'toggle-item', payload: MenuTogglePayload): void;
    }>();

    // ----- state -----
    const slots = useSlots();
    const internalActiveIdMap = ref<MenuIdMap>({});
    const internalExpandedIdMap = ref<MenuIdMap>({});
    const ghMenuRoot = inject<MenuRootContext | null>('ghMenuRoot', null);
    const ghMenuLevel = inject<number>('ghMenuLevel', 0);

    const rootMenuContext: MenuRootContext = ghMenuRoot || {
        internalActiveIdMap,
        internalExpandedIdMap,
        sanitizeIdMap: (sourceMap?: MenuIdMap | null) =>
            sanitizeIdMap(sourceMap),
        pruneMapByLevel: (sourceMap: MenuIdMap | null | undefined, level) =>
            pruneMapByLevel(sourceMap, level),
        isSameIdMap: (
            sourceMapA?: MenuIdMap | null,
            sourceMapB?: MenuIdMap | null
        ) => isSameIdMap(sourceMapA, sourceMapB),
    };

    provide('ghMenuRoot', rootMenuContext);

    // ----- computed -----
    const hasContentSlot = computed(() => {
        return Boolean(slots.content);
    });

    const imageStyle = computed(() => {
        return {
            width: `${props.iconSize}px`,
            height: `${props.iconSize}px`,
        };
    });

    // 判斷目前是否根節點 (由 root 對外提供事件與狀態)
    const isRootMenu = computed(() => {
        return !ghMenuRoot;
    });

    // 目前層級 (level-0 起算)
    const currentLevel = computed(() => {
        const level = Number(ghMenuLevel);
        if (Number.isNaN(level) || level < 0) {
            return 0;
        }

        return Math.floor(level);
    });

    provide('ghMenuLevel', currentLevel.value + 1);

    // activeLevel 防呆，確保為 0 以上整數
    const normalizedActiveLevel = computed(() => {
        const parsedLevel = Number(props.activeLevel);
        if (Number.isNaN(parsedLevel) || parsedLevel < 0) {
            return 0;
        }

        return Math.floor(parsedLevel);
    });

    // 目前渲染使用的 active map
    const renderActiveIdMap = computed<MenuIdMap>(() => {
        if (isRootMenu.value) {
            return internalActiveIdMap.value;
        }

        return rootMenuContext.sanitizeIdMap(
            rootMenuContext.internalActiveIdMap.value
        );
    });

    // 目前渲染使用的 expanded map
    const renderExpandedIdMap = computed<MenuIdMap>(() => {
        if (isRootMenu.value) {
            return internalExpandedIdMap.value;
        }

        return rootMenuContext.sanitizeIdMap(
            rootMenuContext.internalExpandedIdMap.value
        );
    });

    // expandedIdMap 為 null 時維持內部展開狀態；傳入 map 時改為外部受控
    const isExpandedIdMapControlled = computed(() => {
        return props.expandedIdMap !== null;
    });

    // ----- methods -----
    // 判斷 map 內是否存在有效值
    const hasMeaningfulValue = (value: unknown): value is MenuIdValue => {
        return (
            (typeof value === 'string' || typeof value === 'number') &&
            value !== ''
        );
    };

    // 不可選節點仍可展開/收合，但不會寫入 active map
    const isSelectableItem = (item: MenuItem | null | undefined): boolean => {
        return item?.selectable !== false && item?.selectable !== 'false';
    };

    // 將單值與陣列格式統一成去重後清單，供 map 比對與 merge 共用
    const normalizeIdValueList = (
        value: MenuIdMap[string] | null | undefined
    ): MenuIdValue[] => {
        const rawValueList = Array.isArray(value) ? value : [value];
        const valueList: MenuIdValue[] = [];

        rawValueList.forEach((rawValue) => {
            if (!hasMeaningfulValue(rawValue)) {
                return;
            }

            const isDuplicated = valueList.some((valueItem) => {
                return String(valueItem) === String(rawValue);
            });
            if (!isDuplicated) {
                valueList.push(rawValue);
            }
        });

        return valueList;
    };

    // 以字串語意比對 id，避免 number/string 型別差異造成判斷失敗
    const hasIdValue = (
        sourceValue: MenuIdMap[string] | null | undefined,
        targetValue: MenuIdValue | ''
    ): boolean => {
        if (!hasMeaningfulValue(targetValue)) {
            return false;
        }

        return normalizeIdValueList(sourceValue).some((valueItem) => {
            return String(valueItem) === String(targetValue);
        });
    };

    // 清理 map 空值，避免殘留無效層級
    const sanitizeIdMap = (sourceMap?: MenuIdMap | null): MenuIdMap => {
        const safeMap: MenuIdMap = {};
        const mapData = sourceMap || {};

        Object.keys(mapData).forEach((levelKey) => {
            const levelValue = mapData[levelKey];
            if (Array.isArray(levelValue)) {
                const valueList = normalizeIdValueList(levelValue);
                if (valueList.length > 0) {
                    safeMap[levelKey] = valueList;
                }
                return;
            }

            if (hasMeaningfulValue(levelValue)) {
                safeMap[levelKey] = levelValue;
            }
        });

        return safeMap;
    };

    // 比對兩份 id map 是否一致 (忽略空值與型別差異)
    const isSameIdMap = (
        sourceMapA?: MenuIdMap | null,
        sourceMapB?: MenuIdMap | null
    ): boolean => {
        const safeMapA = sanitizeIdMap(sourceMapA);
        const safeMapB = sanitizeIdMap(sourceMapB);
        const mapAKeys = Object.keys(safeMapA).sort();
        const mapBKeys = Object.keys(safeMapB).sort();

        if (mapAKeys.length !== mapBKeys.length) {
            return false;
        }

        return mapAKeys.every((mapKey, index) => {
            const compareKey = mapBKeys[index];
            if (mapKey !== compareKey) {
                return false;
            }

            const mapAValueList = normalizeIdValueList(safeMapA[mapKey])
                .map((valueItem) => String(valueItem))
                .sort();
            const mapBValueList = normalizeIdValueList(safeMapB[compareKey])
                .map((valueItem) => String(valueItem))
                .sort();

            return mapAValueList.join('|') === mapBValueList.join('|');
        });
    };

    // 依目標層級裁切 map，避免舊子層殘留
    const pruneMapByLevel = (
        sourceMap: MenuIdMap | null | undefined,
        level: number
    ): MenuIdMap => {
        const targetLevel = Number(level);
        const safeMap = sanitizeIdMap(sourceMap);
        if (Number.isNaN(targetLevel) || targetLevel < 0) {
            return safeMap;
        }

        const nextMap: MenuIdMap = {};
        Object.keys(safeMap).forEach((levelKey) => {
            if (Number(levelKey) <= targetLevel) {
                nextMap[levelKey] = safeMap[levelKey];
            }
        });

        return nextMap;
    };

    // 寫入指定層級展開值，單展開模式只保留最後一個 id
    const assignExpandedMapLevel = (
        targetMap: MenuIdMap,
        levelKey: string,
        value: MenuIdMap[string] | null | undefined
    ): void => {
        const valueList = normalizeIdValueList(value);
        if (valueList.length === 0) {
            delete targetMap[levelKey];
            return;
        }

        targetMap[levelKey] = props.allowMultipleExpandedParents
            ? valueList
            : valueList[valueList.length - 1];
    };

    // 多展開模式同層使用陣列累加；預設模式維持每層單一展開 id
    const mergeExpandedIdMap = (
        baseMap: MenuIdMap | null | undefined,
        sourceMap: MenuIdMap | null | undefined
    ): MenuIdMap => {
        const nextMap = sanitizeIdMap(baseMap);
        const safeSourceMap = sanitizeIdMap(sourceMap);

        if (!props.allowMultipleExpandedParents) {
            return {
                ...nextMap,
                ...safeSourceMap,
            };
        }

        Object.keys(safeSourceMap).forEach((levelKey) => {
            assignExpandedMapLevel(nextMap, levelKey, [
                ...normalizeIdValueList(nextMap[levelKey]),
                ...normalizeIdValueList(safeSourceMap[levelKey]),
            ]);
        });

        return sanitizeIdMap(nextMap);
    };

    // 單展開模式只保留當前層級以上資料，多展開模式保留完整 map
    const pruneExpandedMapByLevel = (
        sourceMap: MenuIdMap | null | undefined,
        level: number
    ): MenuIdMap => {
        return props.allowMultipleExpandedParents
            ? sanitizeIdMap(sourceMap)
            : pruneMapByLevel(sourceMap, level);
    };

    // 欄位值統一輸出為可渲染字串，空值回傳空字串
    const resolveTextValue = (
        value: string | number | null | undefined
    ): string => {
        if (!hasMeaningfulValue(value)) {
            return '';
        }

        return String(value);
    };

    // 以下 resolver 統一處理欄位 alias 與 fallback，避免 template 內重複判斷
    const resolveItemId = (item: MenuItem): MenuIdValue | '' => {
        const itemValue = item?.value;
        if (!hasMeaningfulValue(itemValue)) {
            return '';
        }

        return itemValue as MenuIdValue;
    };

    // 解析節點顯示名稱
    const resolveItemName = (item: MenuItem): string => {
        return resolveTextValue(item?.label);
    };

    // 解析節點代碼欄位
    const resolveItemCode = (item: MenuItem): string => {
        return resolveTextValue(item?.code);
    };

    // 解析節點 svg 欄位
    const resolveItemSvg = (item: MenuItem): string => {
        return resolveTextValue(item?.svg);
    };

    // 解析節點圖片欄位
    const resolveItemImage = (item: MenuItem): string => {
        return resolveTextValue(item?.image);
    };

    // 解析節點 imageAlt 欄位
    const resolveItemImageAlt = (item: MenuItem): string => {
        return resolveTextValue(item?.imageAlt);
    };

    // 解析節點 title 欄位
    const resolveItemTitle = (item: MenuItem): string => {
        return resolveTextValue(item?.title);
    };

    // 解析節點 signoff 欄位
    const resolveItemSignoff = (item: MenuItem): string => {
        return resolveTextValue(item?.signoff);
    };

    // children 非陣列時回傳空陣列，避免遞迴渲染時出現非預期型別
    const resolveChildren = (item: MenuItem): MenuItem[] => {
        return Array.isArray(item?.children) ? item.children : [];
    };

    // 由 children 清單判斷是否可展開
    const hasChildren = (item: MenuItem): boolean => {
        return resolveChildren(item).length > 0;
    };

    // 有 children 一律當容器節點；無 children 才依 url 決定 a/div
    const resolveNodeTag = (item: MenuItem): string => {
        if (hasChildren(item)) {
            return 'div';
        }

        const itemHref = resolveTextValue(item?.url);
        return itemHref ? 'a' : 'div';
    };

    // 非連結節點不回傳 href，避免產生空連結與誤導可點擊行為
    const resolveNodeHref = (item: MenuItem): string | null => {
        if (resolveNodeTag(item) !== 'a') {
            return null;
        }

        return resolveTextValue(item?.url);
    };

    // 缺少 id 時以層級 + index 組 key，確保遞迴節點穩定渲染
    const resolveItemKey = (item: MenuItem, index: number): string => {
        const itemId = resolveItemId(item);
        return itemId === ''
            ? `${currentLevel.value}-${index}`
            : String(itemId);
    };

    // active 只依 active map 判斷，不受展開狀態影響
    const isActive = (item: MenuItem): boolean => {
        const itemId = resolveItemId(item);
        // active 只依選取路徑判斷，避免展開狀態覆蓋目前定位節點
        const activeIdMap = renderActiveIdMap.value;
        const activeId = activeIdMap[String(currentLevel.value)];
        return hasIdValue(activeId, itemId);
    };

    // expanded 只依 expanded map 判斷，供 UI 切換展開樣式
    const isExpanded = (item: MenuItem): boolean => {
        if (!hasChildren(item)) {
            return false;
        }

        const expandedId =
            renderExpandedIdMap.value[String(currentLevel.value)];
        const itemId = resolveItemId(item);
        return hasIdValue(expandedId, itemId);
    };

    // 清單層級 class 統一由 helper 產生，避免 template 串接字串
    const resolveListClass = (): string[] => {
        return ['gh-menu__list', `gh-menu__list--level-${currentLevel.value}`];
    };

    // item class 聚合 active/expanded 與層級樣式
    const resolveItemClass = (item: MenuItem): Record<string, boolean> => {
        return {
            'gh-menu__item': true,
            [`gh-menu__item--level-${currentLevel.value}`]: true,
            'is-active': isActive(item),
            'is-expanded': isExpanded(item),
        };
    };

    // link class 與 item 保持一致狀態，便於樣式與事件命中
    const resolveLinkClass = (item: MenuItem): Record<string, boolean> => {
        return {
            'gh-menu__link': true,
            [`gh-menu__link--level-${currentLevel.value}`]: true,
            'is-active': isActive(item),
            'is-expanded': isExpanded(item),
        };
    };

    // 依指定層級尋找 activeId，命中後帶回 parent 路徑 map
    const findPathMapByItemIdAndLevel = (
        items: MenuItem[],
        targetId: string,
        targetLevel: number,
        level = 0
    ): MenuIdMap | null => {
        if (
            !Array.isArray(items) ||
            !targetId ||
            Number.isNaN(targetLevel) ||
            targetLevel < 0
        ) {
            return null;
        }

        for (let index = 0; index < items.length; index += 1) {
            const currentItem = items[index];
            const currentItemId = resolveItemId(currentItem);
            const childItems = resolveChildren(currentItem);

            if (
                level === targetLevel &&
                currentItemId !== '' &&
                String(currentItemId) === String(targetId)
            ) {
                return {
                    [String(level)]: currentItemId,
                };
            }

            if (childItems.length === 0 || level >= targetLevel) {
                continue;
            }

            const childPathMap = findPathMapByItemIdAndLevel(
                childItems,
                targetId,
                targetLevel,
                level + 1
            );

            if (!childPathMap) {
                continue;
            }

            if (currentItemId === '') {
                return childPathMap;
            }

            return {
                [String(level)]: currentItemId,
                ...childPathMap,
            };
        }

        return null;
    };

    // 依 activeId + activeLevel 組出完整層級 map
    const resolveActivePathMap = (
        activeId: string | number | '',
        activeLevel: number
    ): MenuIdMap => {
        if (!hasMeaningfulValue(activeId)) {
            return {};
        }

        const targetId = String(activeId);
        const targetLevel = Number(activeLevel);
        const pathMap = findPathMapByItemIdAndLevel(
            props.items,
            targetId,
            targetLevel
        );

        return pathMap ? pathMap : {};
    };

    // 依當前展開狀態補齊路徑，避免資料晚到時 map 遺失
    const buildPathMapFromCurrentState = (
        itemId: MenuIdValue | '',
        level: number
    ): MenuIdMap => {
        const targetLevel = Number(level);
        if (
            !hasMeaningfulValue(itemId) ||
            Number.isNaN(targetLevel) ||
            targetLevel < 0
        ) {
            return {};
        }

        const pathMap: MenuIdMap = {};
        const expandedIdMap = sanitizeIdMap(internalExpandedIdMap.value);
        for (
            let currentIndex = 0;
            currentIndex < targetLevel;
            currentIndex += 1
        ) {
            const parentId = expandedIdMap[String(currentIndex)];
            const parentValue = normalizeIdValueList(parentId)[0];
            if (!hasMeaningfulValue(parentValue)) {
                break;
            }

            pathMap[String(currentIndex)] = parentValue;
        }

        pathMap[String(targetLevel)] = itemId as MenuIdValue;
        return sanitizeIdMap(pathMap);
    };

    // 建立 select payload，統一提供層級 map
    const createSelectPayload = (payload: MenuSelectPayload) => {
        const activeIdMap =
            findPathMapByItemIdAndLevel(
                props.items,
                String(payload.itemId),
                Number(payload.level)
            ) || buildPathMapFromCurrentState(payload.itemId, payload.level);

        const expandedIdMap = pruneExpandedMapByLevel(
            mergeExpandedIdMap(internalExpandedIdMap.value, activeIdMap),
            payload.level
        );

        return {
            ...payload,
            activeIdMap: sanitizeIdMap(activeIdMap),
            expandedIdMap,
        };
    };

    // 產生不變更狀態的 payload (例如不可選節點)，避免誤改 active/expanded
    const createCurrentStatePayload = (payload: MenuSelectPayload) => {
        return {
            ...payload,
            activeIdMap: sanitizeIdMap(internalActiveIdMap.value),
            expandedIdMap: sanitizeIdMap(internalExpandedIdMap.value),
        };
    };

    // 建立 toggle payload，統一更新展開層級
    const createTogglePayload = (
        payload: MenuTogglePayload,
        baseSelectPayload?: ReturnType<typeof createSelectPayload>
    ) => {
        const selectPayload =
            baseSelectPayload ||
            (isSelectableItem(payload.item)
                ? createSelectPayload(payload)
                : createCurrentStatePayload(payload));
        const levelKey = String(payload.level);
        const nextExpandedIdMap = {
            ...selectPayload.expandedIdMap,
        };
        const hasNextExpandedId = Object.prototype.hasOwnProperty.call(
            payload,
            'nextExpandedId'
        );
        const normalizedNextExpandedId =
            payload.hasChildren && !hasNextExpandedId
                ? payload.itemId
                : payload.nextExpandedId;

        if (props.allowMultipleExpandedParents) {
            const currentValueList = normalizeIdValueList(
                nextExpandedIdMap[levelKey]
            );

            if (!hasMeaningfulValue(normalizedNextExpandedId)) {
                assignExpandedMapLevel(
                    nextExpandedIdMap,
                    levelKey,
                    currentValueList.filter((valueItem) => {
                        return String(valueItem) !== String(payload.itemId);
                    })
                );
            } else {
                assignExpandedMapLevel(nextExpandedIdMap, levelKey, [
                    ...currentValueList,
                    normalizedNextExpandedId,
                ]);
            }
        } else if (!hasMeaningfulValue(normalizedNextExpandedId)) {
            delete nextExpandedIdMap[levelKey];
        } else {
            nextExpandedIdMap[levelKey] =
                normalizedNextExpandedId as MenuIdValue;
        }

        return {
            ...selectPayload,
            nextExpandedId: normalizedNextExpandedId,
            expandedIdMap: pruneExpandedMapByLevel(
                nextExpandedIdMap,
                payload.level
            ),
        };
    };

    // 根節點接收 select 後同步狀態，提供 children 正確渲染
    const emitRootSelect = (payload: MenuSelectPayload) => {
        const nextPayload = createSelectPayload(payload);
        internalActiveIdMap.value = { ...nextPayload.activeIdMap };
        internalExpandedIdMap.value = { ...nextPayload.expandedIdMap };
        emits('select-item', nextPayload);
        return nextPayload;
    };

    // 根節點接收 toggle 後同步展開狀態
    const emitRootToggle = (
        payload: MenuTogglePayload,
        baseSelectPayload?: ReturnType<typeof createSelectPayload>
    ) => {
        const nextPayload = createTogglePayload(payload, baseSelectPayload);
        internalExpandedIdMap.value = { ...nextPayload.expandedIdMap };
        emits('toggle-item', nextPayload);
    };

    // 遞迴層 select 事件統一往上拋，根節點補齊 payload
    const handleChildSelect = (payload: MenuSelectPayload): void => {
        if (!payload || !hasMeaningfulValue(payload.itemId)) {
            return;
        }

        if (!isRootMenu.value) {
            emits('select-item', payload);
            return;
        }

        emitRootSelect(payload);
    };

    // 遞迴層 toggle 事件統一往上拋，根節點補齊 payload
    const handleChildToggle = (payload: MenuTogglePayload): void => {
        if (!payload) {
            return;
        }

        if (!isRootMenu.value) {
            emits('toggle-item', payload);
            return;
        }

        emitRootToggle(payload);
    };

    // 點擊節點事件，根節點補齊 map，子節點僅回傳基礎 payload
    const handleItemClick = (item: MenuItem, event: MouseEvent): void => {
        const itemId = resolveItemId(item);
        const itemName = resolveItemName(item);
        const itemCode = resolveItemCode(item);
        const itemHasChildren = hasChildren(item);
        // 先快取目前展開狀態，避免 emit 後被同步狀態覆寫
        const wasExpanded = isExpanded(item);
        // non-collapsible 模式下，點到目前 active 且已展開的父節點時維持展開
        const isCurrentActiveExpanded =
            itemHasChildren && isActive(item) && wasExpanded;
        const nodeTag = resolveNodeTag(item);
        const itemHref = resolveNodeHref(item);
        const itemSelectable = isSelectableItem(item);
        const payload: MenuSelectPayload = {
            item,
            level: currentLevel.value,
            itemId: itemId as MenuIdValue,
            itemName,
            itemCode,
            hasChildren: itemHasChildren,
        };

        // children 或無有效連結時攔截預設行為
        if (nodeTag !== 'a' || itemHasChildren || !itemHref) {
            event.preventDefault();
        }

        if (!hasMeaningfulValue(itemId)) {
            return;
        }

        if (!isRootMenu.value) {
            if (itemSelectable) {
                emits('select-item', payload);
            }

            if (!itemHasChildren) {
                return;
            }

            // 點目前 active path 的上級項目只維持展開，不做收合
            if (!props.collapsible && isCurrentActiveExpanded) {
                return;
            }

            const nextExpandedId = wasExpanded ? '' : itemId;
            emits('toggle-item', {
                ...payload,
                nextExpandedId,
            });
            return;
        }

        const selectPayload = itemSelectable
            ? emitRootSelect(payload)
            : createCurrentStatePayload(payload);

        if (!itemHasChildren) {
            return;
        }

        // 點目前 active path 的上級項目只維持展開，不做收合
        if (!props.collapsible && isCurrentActiveExpanded) {
            return;
        }

        const nextExpandedId = wasExpanded ? '' : itemId;
        emitRootToggle(
            {
                ...payload,
                nextExpandedId,
            },
            selectPayload
        );
    };

    // 由外部 activeId + activeLevel 初始化根節點狀態
    const syncRootStateFromActive = (): void => {
        if (!isRootMenu.value) {
            return;
        }

        const activePathMap = sanitizeIdMap(
            resolveActivePathMap(props.activeId, normalizedActiveLevel.value)
        );
        const currentActivePathMap = sanitizeIdMap(internalActiveIdMap.value);
        const isSameActivePath = isSameIdMap(
            currentActivePathMap,
            activePathMap
        );
        internalActiveIdMap.value = { ...activePathMap };

        if (isExpandedIdMapControlled.value) {
            return;
        }

        // 只有外部 active 路徑真的改變時，才重置 expanded，避免把手動收合覆蓋掉
        if (!isSameActivePath) {
            internalExpandedIdMap.value = props.allowMultipleExpandedParents
                ? mergeExpandedIdMap(internalExpandedIdMap.value, activePathMap)
                : { ...activePathMap };
        }
    };

    // 外部受控 expanded map 變更時，根節點內部狀態需同步
    const syncRootExpandedStateFromProp = (): void => {
        if (!isRootMenu.value || !isExpandedIdMapControlled.value) {
            return;
        }

        internalExpandedIdMap.value = sanitizeIdMap(props.expandedIdMap);
    };

    // ----- watch -----
    watch(
        () => props.activeId,
        () => {
            syncRootStateFromActive();
        }
    );

    watch(
        () => props.activeLevel,
        () => {
            syncRootStateFromActive();
        }
    );

    watch(
        () => props.items,
        () => {
            syncRootStateFromActive();
        },
        {
            deep: true,
        }
    );

    watch(
        () => props.expandedIdMap,
        () => {
            syncRootExpandedStateFromProp();
        },
        {
            deep: true,
        }
    );

    // ----- lifecycle -----
    onBeforeMount(() => {
        syncRootStateFromActive();
        syncRootExpandedStateFromProp();
    });
</script>

<style scoped lang="scss">
    .gh-menu__list {
        margin: 0;
        padding: 0;
        display: flex;
        flex-direction: column;
        gap: 8px;
        list-style: none;

        &--level-0 {
            flex: 1 1 auto;
            height: 100%;
            min-height: 0;
            max-height: 100%;
            padding: 0 8px 0 0;
            overflow-y: auto;
            overflow-x: hidden;
            scrollbar-gutter: stable;

            &::-webkit-scrollbar {
                width: 0.75rem;
                background-color: transparent;
            }

            &::-webkit-scrollbar-track {
                background-color: transparent;
            }

            &::-webkit-scrollbar-thumb {
                background-color: $grayColorE6;
                border-radius: 50px;

                &:hover {
                    background-color: $grayColorD1;
                }
            }

            @supports (-moz-appearance: none) {
                scrollbar-color: $grayColorE6 transparent;
                scrollbar-width: thin;

                &:hover {
                    scrollbar-color: $grayColorD1 transparent;
                }
            }
        }

        &--level-1,
        &--level-2,
        &--level-3 {
            margin-block: 8px;
            padding-left: 24px;
        }
    }

    .gh-menu__item {
        margin: 0;

        > .gh-menu__list {
            display: none;
        }

        &.is-expanded > .gh-menu__list {
            display: flex;
        }

        &.is-active > .gh-menu__link {
            color: $blueColor_2C94FF;
            background-color: $blueColor_EFF6FF;

            .gh-menu__code {
                color: $blueColor_2C94FF;
                background-color: #ffffff;
            }
        }

        &.is-expanded:not(.is-active) > .gh-menu__link {
            background-color: $grayColorF6;
        }

        &.is-expanded > .gh-menu__link {
            .arrow-icon {
                transform: rotate(180deg);
            }
        }
    }

    .gh-menu__link {
        width: 100%;
        margin: 0;
        padding: 6px 8px;
        display: flex;
        align-items: center;
        gap: 8px;
        color: $textColor888;
        border: 1px solid transparent;
        border-radius: 16px;
        text-decoration: none;
        cursor: pointer;
        box-sizing: border-box;

        @media (max-width: 768px) {
            flex-wrap: wrap;
        }

        @media (min-width: 769px) {
            &:hover {
                color: $blueColor_2C94FF;
                background-color: $blueColor_EFF6FF;

                .gh-menu__code {
                    color: $blueColor_2C94FF;
                    background-color: #ffffff;
                }
            }
        }

        .arrow-icon {
            width: 14px;
            height: 14px;
            margin-left: auto;
            flex-shrink: 0;
            transition: transform 0.3s ease;
        }
    }

    .gh-menu__icon {
        flex-shrink: 0;

        &--image {
            object-fit: contain;
        }
    }

    .gh-menu__code {
        flex-shrink: 0;
        font-size: 14px;
        line-height: 1.5;
        white-space: nowrap;

        @media (max-width: 770px) {
            font-size: 12px;
        }
    }

    .gh-menu__name {
        min-width: 0;
        font-size: 16px;
        line-height: 1.5;
        white-space: normal;
        overflow-wrap: anywhere;
        word-break: break-word;

        @media (max-width: 770px) {
            font-size: 14px;
        }
    }

    .gh-menu__signoff {
        margin-left: auto;
        flex-shrink: 0;
        font-size: 16px;
        line-height: 1.5;
        white-space: nowrap;
    }
</style>
